import { KeyboardArrowRight, MoreHoriz } from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  ListItemButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useEntityChildren } from 'api/entity';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import {
  FinsightEntityFieldValue,
  FinsightEntityFieldValueValid,
  FinsightGetEntityFieldsByViewType,
} from 'components/renderField';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useMemo, useState } from 'react';
import { sortEntities, sortFields } from 'utils/fields';

import { EntityListViewProps } from './index.types';

export function EntityListViewView(props: EntityListViewProps) {
  const {
    entity,
    searchParams,
    hideType,
    hideParent,
    disableActions,
    isSelected,
    elemRef,
    onContextMenu,
    onClick,
    onDragStart,
    onMoreButtonClick,
    onFieldChange,
  } = props;

  const entityFieldsToDisplay = useMemo(() => {
    if (!entity || !('description' in entity)) return [];

    return sortFields(Object.values(entity.extraFieldsMeta))
      .map((field) =>
        FinsightGetEntityFieldsByViewType('list', field) ? field : null,
      )
      .filter(
        (x) =>
          x &&
          FinsightEntityFieldValueValid(
            x!,
            entity.extraFields[x!.name],
            'list',
          ),
      )
      .map((field) => ({
        field: field!,
        value: entity.extraFields[field!.name],
      }));
  }, [entity]);

  const workspaceId = useWorkspaceId();
  const theme = useTheme();

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Stack
      ref={elemRef}
      sx={{
        '&:hover > div > .entity-list-view-expand-button': {
          width: 20,
          ml: '-20px',
        },
      }}
    >
      <Stack direction="row">
        <IconButton
          className="entity-list-view-expand-button"
          sx={{
            px: 0,
            width: isExpanded ? 20 : 0,
            ml: isExpanded ? '-20px' : 0,
            transition: 'width 150ms ease, margin 150ms ease',
            overflow: 'hidden',
          }}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <KeyboardArrowRight
            sx={{
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease',
            }}
          />
        </IconButton>
        <ListItemButton
          onContextMenu={onContextMenu}
          onClick={onClick}
          draggable={!disableActions}
          disableRipple
          href={
            disableActions
              ? undefined!
              : `/workspace/${workspaceId}/entity/${entity.id}?${searchParams}`
          }
          onDragStart={onDragStart}
          sx={{
            ...(isSelected
              ? {
                  borderColor: 'primary.main',
                  borderStyle: 'dashed',
                  borderWidth: 1,
                }
              : undefined),
          }}
        >
          {!hideType && <Chip size="small" label={entity.types[0]} />}
          {entityFieldsToDisplay.length > 0 && (
            <Stack gap={1} direction="row" alignItems="center" pr={2}>
              {entityFieldsToDisplay.map(({ field, value }, index) => (
                <FinsightEntityFieldValue
                  key={index}
                  field={field}
                  value={value}
                  viewType="list"
                  onChange={async (v) => {
                    await onFieldChange((field as { name: string }).name, v);
                  }}
                />
              ))}
            </Stack>
          )}
          <Stack direction="row">
            {entity.parents?.length && !hideParent && (
              <Typography mr={2} color="text.secondary">
                {entity.parents
                  ?.toReversed()
                  .map((parent) =>
                    parent.name.length > 10
                      ? `${parent.name.slice(0, 10)}...`
                      : parent.name,
                  )
                  .join(' / ')}
              </Typography>
            )}
            {entityFieldsToDisplay.length > 0 && false && (
              <Typography mr={2}>&mdash;</Typography>
            )}
            <Typography>{entity.name}</Typography>
          </Stack>
        </ListItemButton>
        <IconButton
          disabled={disableActions}
          onClick={(e) => {
            e.preventDefault();
            onMoreButtonClick(e);
          }}
        >
          <MoreHoriz />
        </IconButton>
      </Stack>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{
              height: 0,
            }}
            exit={{
              height: 0,
            }}
            animate={{
              height: isExpanded ? 'auto' : 0,
            }}
            style={{
              borderColor: theme.palette.divider,
              borderWidth: 2,
              borderLeftStyle: 'solid',
              paddingLeft: theme.spacing(2),
              marginLeft: theme.spacing(1),
              overflow: 'hidden',
            }}
          >
            <Stack gap={1}>
              {'description' in entity && entity.description && (
                <Box
                  sx={{
                    py: 1,
                    color: 'text.secondary',
                    '& p': {
                      margin: 0,
                      marginBottom: 1,
                    },
                    '& p:last-child': {
                      marginBottom: 0,
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: entity.description as string,
                  }}
                />
              )}
              <EntityChildren {...props} />
            </Stack>
          </motion.div>
        )}
      </AnimatePresence>
    </Stack>
  );
}

function EntityChildren(props: EntityListViewProps) {
  const { entity, ...rest } = props;
  const { data: _data, isLoading } = useEntityChildren(entity.id);

  const data = useMemo(() => {
    return _data
      ?.filter(
        (x) => x.types[0] !== 'discussion' && x.types[0] !== 'blackframe.file',
      )
      .sort((a, b) => sortEntities(a, b));
  }, [_data]);

  if (isLoading) {
    return (
      <Stack direction="row" alignItems="center" gap={2}>
        <CircularProgress size={16} />
        <Typography color="text.secondary">Loading children...</Typography>
      </Stack>
    );
  }

  if (!data?.length) {
    return (
      <Stack>
        <Typography color="text.secondary">No children</Typography>
      </Stack>
    );
  }

  return (
    <Stack>
      {data?.map((child) => (
        <EntityListViewView
          {...rest}
          key={child.id}
          entity={child}
          hideType={false}
        />
      ))}
    </Stack>
  );
}
